import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { exportStudentsByFilter, getAllStudentWithPaginationService } from '@app/services/student';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
 import { getAllActiveFranchise } from '@app/services/franchise';
import Select from "react-select";
import { useTranslation } from 'react-i18next';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import moment from 'moment';
import fileDownload from 'js-file-download';
import { encodeId } from '@app/helpers/base64url';

const ListStudents = () => {

    const canAccess = useSelector((state: any) => state.auth.permissions);
    const navigate = useNavigate()
    const [t] = useTranslation()
    useEffect(() => {
        if(canAccess.student.list == false){
            toast.error(t('auth.noAccess'))
            navigate('/')
        }
    }, [])

    const selectRef = useRef(null);
    const [students, setStudents] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams()
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(searchParams.get('perPage') ?? 10);
    const [currentPage, setCurrentPage] = useState(searchParams.get('page') ?? 1);
    const [franchises, setFranchises] = useState([]);
    const [franchiseCount, setFranchiseCount] = useState(0);
    const studentStatus = [{label:'Active', value:'active'}, {label:'Inactive', value:'inactive'}]

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };

    const customStylesSelect = {
        dropdownIndicator: () => ({
            display: 'none',
        }),
    }

    useEffect(() => {
        getAllStudentService()
    },[searchParams]);

    useEffect(() => {
        getAllActiveFranchiseData();
    },[]);

    const getAllStudentService = async () => {
        try {
            setLoading(true);
            const studentsResp = await getAllStudentWithPaginationService(searchParams);
            if (studentsResp.success === true) {
                setLoading(false);
                setStudents(studentsResp.collections.data);
                setTotalRows(studentsResp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(studentsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getAllActiveFranchiseData = async () => {
        try {
            const resp = await getAllActiveFranchise();
            if (resp?.success === true) {
                setFranchises(resp?.collections);
                setFranchiseCount(resp?.collections.filter(obj => obj?.enabled_manage_students).length)
            } else {
                toast.error(resp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const columns = [
        {
            name: 'Roll No.',
            selector: (row: { roll_number: string;}) => row.roll_number ?? '---',
            sortable: true,
            sortField: 'roll_number',
            wrap: true,
        },
        {
            name: 'Student Name',
            selector: (row: { first_name: string; middle_name?: string; last_name: string; }) => 
                [row.first_name, row.middle_name, row.last_name].filter(Boolean).join(' '),
            sortable: true,
            sortField: 'first_name',
            wrap: true,
            minWidth: '200px',
        },
        {
            name: 'Gender',
            selector: (row: { gender:string; }) => row.gender.charAt(0).toUpperCase(),
            sortable: true,
            sortField: 'gender',
            wrap: true,
        },
        {
            name: 'DOB',
            selector: (row: { date_of_birth: string; }) => row.date_of_birth ? row.date_of_birth : '---',
            sortable: true,
            sortField: 'date_of_birth',
        },
        // {
        //     name: 'School',
        //     selector: (row: { school_name: string; }) => row.school_name ? row.school_name : '---',
        //     sortable: true,
        //     sortField: 'school_name',
        // },
        {
            name: 'Franchise',
            selector: (row: { franchise_name: string; }) => row.franchise_name ? row.franchise_name : '---',
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
        },
        {
            name: 'Centre',
            selector: (row: { centre_name: string; }) => row.centre_name ? row.centre_name : '---',
            sortable: true,
            sortField: 'centre_name',
            wrap: true,
        },
        {
            name: 'Camp Only',
            selector: (row: { camp_only:string; }) => row.camp_only,
            cell: (row: { camp_only: boolean }) => (
                row.camp_only ? <span className="badge bg-success">Yes</span> : <span className="capitalize badge bg-danger">No</span>
            ),
            sortable: true,
            sortField: 'camp_only',
            wrap: true,
            omit: !canAccess?.student?.['enroll-in-camp'],
        },
        {
            name: 'Status',
            selector: (row: { status: string; }) => row.status,
            cell: (row: { status: string; }) => (
                row.status === 'active' ? <span className="badge bg-success">Active</span> : <span className="capitalize badge bg-danger">{row.status}</span>
            ),
            sortable: true,
            sortField: 'status',
        },
        {  
            name: 'Action',
            cell: (row: { id: number, status: string, student_local_printing: boolean, camp_only: boolean, enabled_holiday_camp: boolean, enabled_adhoc_printing:boolean}) => (
              <>
                { canAccess?.student?.edit &&
                    <Link
                    to={`/student/edit/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="Edit Student Details"
                    >
                    <i className="fas fa-pen mr-2"/>
                    </Link>
                }
                { canAccess?.student?.view &&
                    <Link
                    to={`/student/view/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="View Student Details"
                    >
                    <i className="fas fa-eye mr-2"/>
                    </Link>
                }
                { canAccess?.student?.['set-work'] && row.status === 'active' && !row.camp_only &&
                    <Link
                        to={`/student/set-work/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        data-toggle="tooltip" title="Set Work"
                    >
                        <i className="fas fa-network-wired mr-2"/>
                    </Link>
                }
                { canAccess?.student?.['work-summary'] && !row.camp_only &&
                    <Link
                        to="/student/work-summary"
                        role="button"
                        state={{ id: row.id }}
                        data-toggle="tooltip" title="Work Summary"
                    >
                        <i className="fas fa-book-reader mr-2"/>
                    </Link>
                }
                { canAccess?.['student-auto-marking']?.list && !row.camp_only &&
                    <Link
                        to={`/student/${encodeId(row.id)}/auto-marking`}
                        role="button"
                        data-toggle="tooltip" title="View Auto Marking History"
                    >
                        <i className="fas fa-robot mr-2"/>
                    </Link>
                }
                {/* { canAccess?.student?.['work-history'] &&
                    <Link
                        to={`/student/work-history/${row.id}`}
                        role="button"
                        state={{ id: row.id }}
                        data-toggle="tooltip" title="Work History"
                    >
                        <i className="fas fa-history mr-2" />
                    </Link>
                } */}
                { canAccess?.student?.['adhoc-printing'] && row.enabled_adhoc_printing && row.status === 'active' &&
                    <Link
                        to={`/student/adhoc-printing/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        data-toggle="tooltip" title="Adhoc Printing"
                    >
                        <i className="fas fa-print mr-2" />
                    </Link>
                }

                { canAccess?.student?.['enroll-in-camp'] && row.enabled_holiday_camp && row.status === 'active' &&
                    <Link
                        to={`/student/enroll-in-camp/${encodeId(row.id)}`}
                        role="button"
                        state={{ id: row.id }}
                        data-toggle="tooltip" title="Enroll In The Camp"
                    >   
                        <i className="fas fa-chalkboard-teacher"/>
                    </Link>
                }
              </>
            ),
            minWidth: '14%'
        },
    ];

    const handlePageChange = page => {
        searchParams.set('page', page)
        setSearchParams(searchParams)
        
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        searchParams.set('perPage', newPerPage)
        setSearchParams(searchParams)
        
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        searchParams.set('sortField', column.sortField)
        searchParams.set('sortDirection', sortDirection)
        setSearchParams(searchParams)
    };

    const handleResetFilter = async () => {
        selectRef.current.setValue('');
        searchParams.delete('firstName')
        searchParams.delete('lastName')
        searchParams.delete('franchiseId')
        searchParams.delete('campOnly')
        searchParams.delete('status')
        setSearchParams(searchParams)  
    };

    const onChangeFranchise = (option) => {
        searchParams.set('franchiseId', option == null ? '' : option.id )
        setSearchParams(searchParams)
    };

    const exportCSV = async() => {
        try {
            setLoading(true);
            let records = await exportStudentsByFilter(searchParams)
            if (records.success === true) {
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(records?.errors[0]?.msg);
                return;
            }

            if((records?.collections).length === 0){
                toast.error('Records not found.')
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet("Students");
            const worksheet = workbook.getWorksheet('Students');
            worksheet.columns = [
                { header: "Roll No", key: "roll_no" },
                { header: "First Name", key: "first_name" },
                { header: "Middle Name", key: "middle_name" },
                { header: "Last Name", key: "last_name" },
                { header: "Gender", key: "gender" },
                { header: "DOB", key: "dob" },
                { header: "Grade", key: "grade" },
                { header: "School", key: "school" },
                { header: "Franchise", key: "franchise" },
                { header: "Centre", key: "centre" },
                { header: "Address", key: "address_line_1" },
                { header: "Address Line 2", key: "address_line_2" },
                { header: "City", key: "city" },
                { header: "Postcode", key: "postcode" },
                { header: "Country", key: "country" },
                { header: "Status", key: "status" },
                { header: "Invoice Type", key: "invoice_type" },
                { header: "Holiday Camp Only", key: "holiday_camp_only" },
                { header: "Parent(s)", key: "parents" },

                { header: "English Rate", key: "english_rate" },
                { header: "English VAT", key: "english_vat" },
                { header: "English Start", key: "english_start" },
                { header: "English End", key: "english_end" },

                { header: "Maths Rate", key: "maths_rate" },
                { header: "Maths VAT", key: "maths_vat" },
                { header: "Maths Start", key: "maths_start" },
                { header: "Maths End", key: "maths_end" },

                { header: "Science Rate", key: "science_rate" },
                { header: "Science VAT", key: "science_vat" },
                { header: "Science Start", key: "science_start" },
                { header: "Science End", key: "science_end" },
            ];
            
            (records?.collections).map((row) => {
                row.roll_no = row.roll_no !== null ? row.roll_no : '---';
                row.gender = row.gender !== null ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : '---';
                row.dob = row.dob !== null ? moment(row.dob).format('DD/MM/YYYY') : '---';
                row.franchise = row.franchise !== null ? row.franchise : '---';
                row.centre = (row.centre != null) ? row.centre : '---';
                row.school = (row.school != null) ? row.school : '---';
                row.grade = (row.grade != null) ? row.grade : '---';
                row.address_line_2 = (row.address_line_2 != null) ? row.address_line_2 : '---';
                row.city = (row.city != null) ? row.city : '---';
                row.postcode = (row.postcode != null) ? row.postcode : '---';
                row.country = (row.country != null) ? row.country : '---';
                row.status = row.gender !== null ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : '---';
                row.invoice_type = row.invoice_type !== null ? row.invoice_type.charAt(0).toUpperCase() + row.invoice_type.slice(1) : '---';
                row.holiday_camp_only = row.holiday_camp_only ? 'Yes' : 'No';
                row.parents = row.parents != null ? row.parents  : '---';

                row.english_rate = (row.english_rate != null) ? row.english_rate : 'N/A';
                row.english_vat = (row.english_vat != null) ? row.english_vat : 'N/A';
                row.english_start = (row.english_start != null) ? moment(row.english_start).format('DD/MM/YYYY') : 'N/A';
                row.english_end = (row.english_end != null) ? moment(row.english_end).format('DD/MM/YYYY') : 'N/A';

                row.maths_rate = (row.maths_rate != null) ? row.maths_rate : 'N/A';
                row.maths_vat = (row.maths_vat != null) ? row.maths_vat : 'N/A';
                row.maths_start = (row.maths_start != null) ? moment(row.maths_start).format('DD/MM/YYYY') : 'N/A';
                row.maths_end = (row.maths_end != null) ? moment(row.maths_end).format('DD/MM/YYYY') : 'N/A';

                row.science_rate = (row.science_rate != null) ? row.science_rate : 'N/A';
                row.science_vat = (row.science_vat != null) ? row.science_vat : 'N/A';
                row.science_start = (row.science_start != null) ? moment(row.science_start).format('DD/MM/YYYY') : 'N/A';
                row.science_end = (row.science_end != null) ? moment(row.science_end).format('DD/MM/YYYY') : 'N/A';
            })

            worksheet.addRows(records?.collections);
            let filename = 'Students-' + moment().format('DD-MMM-YYYY') + '.csv';

            workbook.csv.writeBuffer().then(function(buffer) {
                fileDownload(buffer, filename);
            });
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Manage Students</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Manage Students</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Manage Students</h3>
                                        <div className="col-lg-12 text-right">
                                            { canAccess?.student?.create && franchiseCount > 0 &&
                                                <Link className="btn btn-primary" to="/add-student" > + Add New Student </Link>
                                            }
                                            { franchiseCount == 0 &&
                                                <div className='text-danger'>
                                                    <i className="icon fas fa-exclamation-triangle" />
                                                    This panel will be live shortly
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row align-items-center">
                                            <div className="col-sm-2 mb-2">
                                                <input type="text" name='firstName' className="form-control" value={searchParams.get('firstName') ?? ''} onChange={(e) => {searchParams.set('firstName', e.target.value); setSearchParams(searchParams)}} placeholder="Search By First Name" />
                                            </div>
                                            <div className="col-sm-2 mb-2">
                                                <input type="text" name='lastName' className="form-control" value={searchParams.get('lastName') ?? ''} onChange={(e) => {searchParams.set('lastName', e.target.value); setSearchParams(searchParams)}} placeholder="Search By Last Name" />
                                            </div>
                                            <div className="col-sm-2 mb-2">
                                                <Select
                                                    ref={selectRef}
                                                    name="franchiseId"
                                                    placeholder="Search By Franchise"
                                                    options={franchises}
                                                    getOptionValue={(option)=>`${option['id']}`}
                                                    getOptionLabel={(option)=>`${option['name']}`}
                                                    onChange={ (option) => {onChangeFranchise(option)}}
                                                    value={franchises.filter(function(option) {
                                                        return option.id == searchParams.get('franchiseId');
                                                    })}
                                                    isClearable={true}
                                                    styles={customStylesSelect}
                                                />
                                            </div>
                                            <div className="col-sm-2 mb-2">
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={(e) => {  searchParams.set('status', e.target.value); setSearchParams(searchParams)}}
                                                    value={searchParams.get('status') ?? ''}
                                                >
                                                    <option value="">Search By Status</option>
                                                    {studentStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            { canAccess?.student?.['enroll-in-camp'] && (
                                            <div className="col-sm-2 mb-2 d-flex align-items-center">
                                                <div className="icheck-primary">
                                                    <input 
                                                        type="checkbox" 
                                                        className="form-check-input" 
                                                        checked={searchParams.get('campOnly') == 'true' ? true : false}
                                                        id="camp_only"
                                                        name="camp_only"
                                                        onChange={(e) => { searchParams.set('campOnly', e.target.checked ? 'true' : 'false' ); setSearchParams(searchParams)}}
                                                    />
                                                    <label htmlFor="camp_only" className="form-check-label ml-1">
                                                        Holiday Camp Only
                                                    </label>
                                                </div>
                                            </div>
                                            )}
                                            <div className="col-sm-4 mb-2">
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                                { canAccess?.student['export-csv'] &&
                                                    <Button type="submit" isLoading={isLoading} onClick={exportCSV} className="btn btn-success"><i className="fas fa-file-csv"></i> Export</Button>
                                                }
                                            </div>
                                        </div>

                                        <DataTable
                                            columns={columns}
                                            data={students}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={Number(currentPage)}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={Number(perPage)}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>

        </div>
    )
};

export default ListStudents;